/**
 * Shared progress computation for the report-generation dialogs.
 */

/**
 * Minimum time between "the user started a run" and "we land them on the
 * finished report".
 *
 * The backend deduplicates to one report per company per month, so a "run"
 * for a month that already has a completed report resolves in ~2 seconds —
 * technically correct, but it reads as fake ("did it actually check
 * anything?"). Both the progress dialog (staged bar) and the shell's
 * completion navigation honour this floor, measured from the claim in
 * `setGenerateDialogRequest`. Runs that genuinely take minutes are past the
 * floor by the time they finish and are unaffected.
 */
export const MIN_GENERATION_DWELL_MS = 6000;

export interface ProgressData {
  status?: "in_progress" | "completed" | "error" | "finalizing";
  created_ts?: string;
}

/**
 * Compute fake progress percentage with time-based progression and status awareness.
 * This creates a more natural UX by:
 * - Showing 100% for completed reports
 * - Showing 93% for finalizing reports
 * - For in-progress: showing time-based progress (assumes 6-minute duration, caps at 93%)
 * @param data - Report object with status and created_ts
 * @returns Percentage (0-100)
 */
export function computeFakeProgressPercent(data: ProgressData | null | undefined): number {
  if (!data) {
    return 0;
  }

  const status = data.status;

  // Completed reports always show 100%
  if (status === "completed") {
    return 100;
  }

  // Finalizing reports show 93%
  if (status === "finalizing") {
    return 93;
  }

  // For in-progress reports, calculate time-based progress
  if (status === "in_progress" && data.created_ts) {
    const createdTime = new Date(data.created_ts).getTime();
    const now = Date.now();
    const ageMs = now - createdTime;

    // Time-based progress: assume 6-minute duration, cap at 93%
    const SIX_MINUTES_MS = 6 * 60 * 1000;
    const progressPercent = Math.min(93, Math.round((ageMs / SIX_MINUTES_MS) * 100));
    return Math.max(0, progressPercent);
  }

  // Fallback for in-progress without timestamp or other states
  if (status === "in_progress") {
    return 0;
  }

  return 0;
}
